import { FlyoutListMenuWidget, MenuItemInfo } from '@tableau/widgets';
import * as React from 'react';
import * as TypeStyle from 'typestyle';
import { GenericToolbarItem } from '../types/GenericToolbarItem';
import { Button, ButtonProps } from './Button';
import { IconFromId, ToolbarIconId } from './Icons';

const OverflowDropdownStyleName = TypeStyle.style({
  $nest: {
    '.dropdown-arrow': {
      display: 'none',
    },
  },
});

const TallMenuItemsStyleName = TypeStyle.style({
  $nest: {
    '& [role^="menuitem"] > :not(.hasdetail)': {
      height: 30,
      lineHeight: '30px',
    },
  },
});

export interface DropdownProps extends Omit<ButtonProps, 'buttonRef' | 'buttonType' | 'onClick'> {
  /** Items to display in the dropdown menu */
  dropdownItems: GenericToolbarItem[];
  /** Set to true for overflow dropdown styling */
  isOverflow?: boolean;
}

export function Dropdown(props: DropdownProps): React.ReactElement {
  const [buttonEl, setButtonEl] = React.useState<HTMLButtonElement | null>(null);
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const menuId = `viz-viewer-toolbar-${props.id}-menu`;

  const { description, dropdownItems, isOverflow, ...buttonProps } = props;

  const menuItemInfos = React.useMemo(() => convertToolbarItems(dropdownItems, isOverflow), [dropdownItems, isOverflow]);

  const handleButtonClick = (): void => setIsMenuOpen(!isMenuOpen);
  const handleMenuClose = React.useCallback(() => setIsMenuOpen(false), []); // useCallback so the pointer is stable for cleanup in useEffect
  const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>): void => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      e.stopPropagation();
      setIsMenuOpen(true);
    }
  };

  const handleSelect = (value: string): void => {
    dropdownItems.forEach((item) => {
      if (item.testId === value && !item.disabled) {
        setIsMenuOpen(false);
        item.onClick();
      }
    });
  };

  // make sure dropdown menu is closed when entering/exiting fullscreen
  React.useEffect(() => {
    document.addEventListener('fullscreenchange', handleMenuClose);
    return (): void => document.removeEventListener('fullscreenchange', handleMenuClose);
  }, [handleMenuClose]);

  if (dropdownItems.length === 0) {
    return <></>;
  }

  return (
    <>
      <Button
        {...buttonProps}
        aria-controls={isMenuOpen ? menuId : undefined}
        aria-haspopup={'menu'}
        aria-expanded={isMenuOpen}
        buttonRef={setButtonEl}
        buttonType={'dropdown'}
        className={isOverflow ? OverflowDropdownStyleName : undefined}
        description={isMenuOpen ? undefined : description} // suppress the tooltip when the menu is open
        onClick={handleButtonClick}
        onKeyDown={handleKeyDown}
        selected={isMenuOpen}
      />
      {isMenuOpen && (
        <FlyoutListMenuWidget
          aria-labelledby={buttonProps.id}
          allowAutoPosition={true}
          anchorEl={buttonEl}
          className={TallMenuItemsStyleName}
          id={menuId}
          menuItemInfos={menuItemInfos}
          menuStyle={{ maxWidth: 316, minWidth: buttonEl!.getBoundingClientRect().width, width: 'max-content' }}
          onSelect={handleSelect}
          requestCloseAll={handleMenuClose}
          testId={`${props.testId}-flyout`}
          toggleEl={buttonEl}
        />
      )}
    </>
  );
}

function convertToolbarItems(items: Array<GenericToolbarItem>, hideDescription?: boolean): Array<MenuItemInfo> {
  const anythingChecked = items.some((item) => item.selected);
  // W-17210432 Menu items with detail and checked state are not yet supported.
  // For now, do not show selected state for lists with any descriptions.
  const anythingWithDescription = items.some((item) => item.description);

  return items.map((item) => ({
    annotation: undefined,
    checked: anythingChecked && !anythingWithDescription ? !!item.selected : undefined,
    detail: hideDescription ? undefined : item.description,
    disabled: item.disabled,
    displayValue: item.label,
    icon: IconFromId[item.iconId as ToolbarIconId],
    isDivider: undefined, // item.isSeparator???
    isGroup: item.isHeader,
    menuItemInfos: undefined,
    value: item.testId,
  }));
}
